/**
 * Хук для работы с сообщениями: список бесед, сообщения пациента, информация о пациенте
 * Поддерживает фильтрацию по каналам и отправку сообщений через выбранный канал
 * Дата: 28.12.2025 - Добавлена поддержка tenantId для SuperAdmin (TODO-016)
 * Дата: 08.01.2026 - Добавлен infinite scroll для списка бесед
 * Дата: 15.01.2026 - TODO-018b: fallback polling если WS недоступен (через WebSocketContext)
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/api/messages.ts` — HTTP API
 * - `frontend/src/contexts/WebSocketContext.tsx` — статус WS (connected/offline)
 * - `frontend/src/pages/Messages.tsx` — основной потребитель (список бесед/чат)
 *
 * СХЕМА:
 * - Композиция функций:
 *   - useMessages -> useInfiniteQuery(conversations) + useQuery(messages/patient) + useMutation(send)
 * - Схема вызовов:
 *   - wsStatus=offline -> refetchInterval=5s -> polling вместо realtime
 */
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { messagesApi } from '@/api/messages';
import { useWebSocketContext } from '@/contexts/WebSocketContext';

const PAGE_SIZE = 30;
// 14.03.26 - Историю чата грузим страницами, а не "срезом на 1000", чтобы UI вёл себя
// одинаково для всех каналов и мог дочитывать старые сообщения по скроллу.
const CHAT_HISTORY_PAGE_SIZE = 100;

// Дата: 27.01.2026 - Добавлен search параметр для поиска по телефону, ФИО, TG/MAX ID
// Дата: 15.03.2026 - Вкладки sidebar фильтруем на сервере, чтобы SMS/MAX/TG не пустели из-за первых 30 all-диалогов
export function useMessages(
  phone?: string,
  channel?: string,
  tenantId?: string,
  listChannel?: string,
  search?: string,
  listChatType?: string
) {
  const queryClient = useQueryClient();
  // Дата: 15.01.2026 - TODO-018b: fallback на polling если WS недоступен
  const { status: wsStatus } = useWebSocketContext();
  const refetchInterval = wsStatus === 'connected' ? false : 5000;
  
  // Список бесед с infinite scroll
  // Дата: 08.01.2026 - Переписано на useInfiniteQuery для пагинации
  // Дата: 10.01.2026 - Исправлено: не делаем запрос если tenantId пустой (SuperAdmin без выбора)
  // Дата: 27.01.2026 - Добавлен search в queryKey и queryFn
  const conversationsQuery = useInfiniteQuery({
    queryKey: ['conversations', tenantId, listChannel, search, listChatType],
    queryFn: async ({ pageParam = 0 }) => {
      return messagesApi.getConversations(tenantId, listChannel, pageParam, PAGE_SIZE, search, listChatType);
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, page) => sum + page.conversations.length, 0);
      // Если загрузили меньше чем total, есть ещё страницы
      if (totalLoaded < lastPage.total) {
        return totalLoaded; // offset для следующей страницы
      }
      return undefined; // Больше страниц нет
    },
    initialPageParam: 0,
    enabled: !phone && !!tenantId, // Не загружать без tenantId
    refetchInterval,
  });
  
  // Сообщения конкретного пациента
  // Дата: 28.12.2025 - Добавлен tenantId для SuperAdmin
  // Дата: 10.01.2026 - Исправлено: не делаем запрос если tenantId пустой
  const messagesQuery = useInfiniteQuery({
    queryKey: ['messages', phone, channel, tenantId],
    queryFn: async ({ pageParam = 0 }) =>
      messagesApi.getConversationMessages(phone!, channel, tenantId, CHAT_HISTORY_PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, page) => sum + page.messages.length, 0);
      if (totalLoaded < lastPage.total) {
        return totalLoaded;
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: !!phone && !!tenantId,
    refetchInterval: phone ? refetchInterval : false,
  });
  
  // Информация о пациенте + привязки
  // Дата: 10.01.2026 - Исправлено: не делаем запрос если tenantId пустой, передаем tenantId
  // Дата: 10.01.2026 - Поддержка поиска по telegram ID (tg:USER_ID):
  //   - При работе с Telegram Bot/Client мы получаем telegram user ID, не телефон
  //   - Backend GetBindings теперь понимает формат tg:USER_ID и ищет по messenger_id
  //   - Для tg: не ищем пациента напрямую (404), но ищем bindings
  // Дата: 25.01.2026 - Добавлена поддержка MAX ID (max: или числовой ID не похожий на телефон)
  const isTelegramId = phone?.startsWith('tg:');
  const isMaxId = phone?.startsWith('max:');
  // Числовой ID, который НЕ телефон: не начинается с 7 или длина не 11 цифр
  const isNumericMessengerId = phone && /^\d+$/.test(phone) && (phone.length < 11 || !phone.startsWith('7'));
  const isMessengerId = isTelegramId || isMaxId || isNumericMessengerId;
  
  const patientQuery = useQuery({
    queryKey: ['patient', phone, tenantId],
    queryFn: async () => {
      const [patient, bindings] = await Promise.all([
        // Для messenger ID не ищем пациента напрямую - только через bindings
        isMessengerId 
          ? Promise.resolve(null) 
          : messagesApi.getPatientInfo(phone!, tenantId).catch(() => null),
        messagesApi.getPatientBindings(phone!, tenantId).catch(() => ({ bindings: [] })),
      ]);
      return { patient, bindings: bindings.bindings || [] };
    },
    enabled: !!phone && !!tenantId,
    refetchInterval: phone ? refetchInterval : false,
  });
  
  // Отправка сообщения
  // Дата: 10.01.2026 - Добавлен tenantId для SuperAdmin
  const sendMutation = useMutation({
    mutationFn: (data: {
      phone: string;
      channel: string;
      content: string;
      content_type: string;
      ai_source?: 'ai_clean' | 'ai_edited' | null;
      ai_suggestion_id?: string | null;
      ai_event_id?: string | null;
    }) => messagesApi.sendMessage({ ...data, tenant_id: tenantId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', phone] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
  
  // Дата: 26.01.2026 - Отметить сообщения прочитанными (по каналу или все)
  const markAsReadMutation = useMutation({
    mutationFn: (data: { phone: string; channel?: string }) =>
      messagesApi.markAsRead(data.phone, tenantId, data.channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', phone] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
  
  // Объединить все страницы бесед в один массив
  const allConversations = conversationsQuery.data?.pages.flatMap(page => page.conversations) || [];
  const totalConversations = conversationsQuery.data?.pages[0]?.total || 0;
  
  return {
    // Conversations
    conversations: allConversations,
    totalConversations,
    
    // Infinite scroll helpers
    hasMoreConversations: conversationsQuery.hasNextPage,
    fetchMoreConversations: conversationsQuery.fetchNextPage,
    isFetchingMore: conversationsQuery.isFetchingNextPage,
    
    // Messages
    messages: messagesQuery.data?.pages
      ? [...messagesQuery.data.pages]
          .reverse()
          .flatMap((page) => page.messages)
      : [],
    totalMessages: messagesQuery.data?.pages[0]?.total || 0,
    hasMoreMessages: messagesQuery.hasNextPage,
    fetchMoreMessages: messagesQuery.fetchNextPage,
    isFetchingMoreMessages: messagesQuery.isFetchingNextPage,
    
    // Patient info
    patient: patientQuery.data?.patient,
    bindings: patientQuery.data?.bindings || [],
    
    // Loading states
    isLoading: phone ? messagesQuery.isLoading : conversationsQuery.isLoading,
    
    // Actions
    sendMessage: sendMutation.mutateAsync,
    isSending: sendMutation.isPending,
    
    // Дата: 26.01.2026 - Отметить прочитанными
    markAsRead: markAsReadMutation.mutateAsync,
    isMarkingAsRead: markAsReadMutation.isPending,
  };
}

// Дата: 09.01.2026 - TODO-019a: Хук для получения fallback цепочки
export function useFallbackChain(messageId?: string, tenantId?: string) {
  return useQuery({
    queryKey: ['fallback-chain', messageId, tenantId],
    queryFn: () => messagesApi.getFallbackChain(messageId!, tenantId),
    enabled: !!messageId,
  });
}
